import { PayeeAccountTypesEnum } from "@propertelligent/client-api";
import { ALL_FILTER_KEY } from "../../../hooks/useAggregateFilterCounts";
import { escapeDynamicLinqStringLiteral } from "../../../utils/dynamicLinq";

export { ALL_FILTER_KEY, escapeDynamicLinqStringLiteral };

/**
 * The trailing "catch-all" pill. Its rows are the *remainder* (everything that is not one of
 * the explicit types below), not `PayeeAccountTypeId == PayeeAccountTypesEnum.Other`.
 */
export const OTHER_FILTER_KEY = "Other";

// Deliberately excludes PayeeAccountTypesEnum.Other -- "Other" is handled as a true
// remainder (see EXPLICIT_PAYEE_TYPE_IDS below), not a literal PayeeAccountTypeId == 999
// filter, so it must contain exactly the same 10 ids as the backend's
// PayeeAccountAggregateRegistration.GetTypeCountAggregates() or the Other pill's count
// and the rows it actually filters to will silently diverge. "Organization User" and
// "Contractor" are included here (even though PayeeAccountTypesEnum hides them from the
// manual-create dropdown) so their pills filter correctly and so the "Other" remainder
// below automatically excludes their rows too.
export const PAYEE_ACCOUNT_TYPE_MAP: Record<string, PayeeAccountTypesEnum> = {
  "Utility Company": PayeeAccountTypesEnum.UtilityCompany,
  "Tax Authority": PayeeAccountTypesEnum.TaxAuthority,
  "Insurance Company": PayeeAccountTypesEnum.InsuranceCompany,
  "Bank Account": PayeeAccountTypesEnum.BankAccount,
  "Credit Card": PayeeAccountTypesEnum.CreditCard,
  "Legal/Lawyer": PayeeAccountTypesEnum.LegalLawyer,
  "Community/Association": PayeeAccountTypesEnum.CommunityAssociation,
  Vendor: PayeeAccountTypesEnum.Vendor,
  "Organization User": PayeeAccountTypesEnum.OrganizationUser,
  Contractor: PayeeAccountTypesEnum.Contractor,
};

export const EXPLICIT_PAYEE_TYPE_IDS = Object.values(PAYEE_ACCOUNT_TYPE_MAP);

export const buildPayeeAccountFilter = (filter: string, search: string): string | null => {
  let baseFilter = "";
  if (filter === OTHER_FILTER_KEY) {
    baseFilter = EXPLICIT_PAYEE_TYPE_IDS.map((id) => `PayeeAccountTypeId != ${id}`).join(" && ");
  } else if (filter !== ALL_FILTER_KEY) {
    const enumId = PAYEE_ACCOUNT_TYPE_MAP[filter];
    if (enumId !== undefined) {
      baseFilter = `PayeeAccountTypeId == ${enumId}`;
    }
  }

  const searchFilter = search
    ? `AccountName.ToLower().Contains("${escapeDynamicLinqStringLiteral(search.toLowerCase())}")`
    : "";

  const combinedFilter = [baseFilter, searchFilter].filter(Boolean).join(" && ");
  return combinedFilter || null;
};
